import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import type { ClassroomSummary } from '../../shared/api/types';
import { Alert, Button, Card, ErrorState, Field, Loading } from '../../shared/ui/components';
import { SearchSelect } from '../../shared/ui/SearchSelect';
import { useAsync } from '../../shared/ui/useAsync';
import { ClassroomCard } from './shared';

type Option = { id: string; name: string; kids: boolean };

/**
 * Formulario «Nuevo salón»: el de la ficha del ministerio (un solo ministerio, sin elegir) y el de Raíces
 * (elige ministerio y, si se quiere, nombra al maestro de una vez).
 */
export function NewClassroomForm({ churchId, ministries, candidates, onCancel }: { churchId: string; ministries: Option[]; candidates?: { personId: string; name: string }[]; onCancel: () => void }) {
  const navigate = useNavigate();
  const [ministryId, setMinistryId] = useState(ministries.length === 1 ? ministries[0]!.id : '');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const kids = ministries.find((m) => m.id === ministryId)?.kids ?? false;

  async function create(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    const num = (k: string) => (String(form.get(k) ?? '').trim() === '' ? null : Number(form.get(k)));
    if (!ministryId) {
      setError('Elige el ministerio del salón.');
      return;
    }
    const teacherId = String(form.get('teacherId') ?? '');
    setBusy(true);
    setError(null);
    try {
      const { id } = await api.post<{ id: string }>(`/organization/churches/${churchId}/ministries/${ministryId}/classrooms`, {
        name: String(form.get('name') ?? '').trim(),
        description: String(form.get('description') ?? '').trim() || null,
        minAge: num('minAge'),
        maxAge: num('maxAge'),
      });
      if (teacherId) await api.put(`/organization/churches/${churchId}/classrooms/${id}/teachers/${teacherId}`, {});
      navigate(`/app/salones/${id}`);
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  }

  return (
    <Card title="Nuevo salón">
      <form className="form" onSubmit={create} noValidate>
        {error ? <Alert>{error}</Alert> : null}
        <Field label="Nombre del salón" name="name" required maxLength={80} placeholder={kids ? 'Exploradores' : 'Adultos'} />
        {ministries.length > 1 ? (
          <div className="field">
            <label htmlFor="room-ministry">Ministerio</label>
            <select id="room-ministry" value={ministryId} onChange={(e) => setMinistryId(e.target.value)} required>
              <option value="">Elige…</option>
              {ministries.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
          </div>
        ) : null}
        {candidates ? (
          <div className="field">
            <label htmlFor="room-teacher">Maestro (opcional)</label>
            <SearchSelect id="room-teacher" name="teacherId" options={candidates.map((p) => ({ value: p.personId, label: p.name }))} />
          </div>
        ) : null}
        <Field label="Descripción (opcional)" name="description" maxLength={1000} />
        <div className="form__row">
          <Field label={kids ? 'Edad desde' : 'Edad desde (opcional)'} name="minAge" type="number" min={0} max={120} />
          <Field label={kids ? 'Edad hasta' : 'Edad hasta (opcional)'} name="maxAge" type="number" min={0} max={120} />
        </div>
        <div className="actions">
          <Button type="submit" busy={busy}>
            Crear salón
          </Button>
          <Button type="button" variant="ghost" onClick={onCancel}>
            Cancelar
          </Button>
        </div>
      </form>
    </Card>
  );
}

/** Salones de Raíces o Raíces Kids, dentro de la ficha del ministerio. */
export function ClassroomsCard({ churchId, ministryId, kids }: { churchId: string; ministryId: string; kids: boolean }) {
  const data = useAsync(
    (signal) => api.get<{ items: ClassroomSummary[]; canCreate: boolean; withoutBirthDate?: { personId: string; name: string }[] }>(`/organization/churches/${churchId}/classrooms?ministryId=${ministryId}`, signal),
    [churchId, ministryId],
  );
  const [adding, setAdding] = useState(false);

  return (
    <section className={`ministry-section${kids ? ' kids' : ''}`} aria-labelledby="rooms-title">
      <h2 id="rooms-title">{kids ? 'Salones por edad' : 'Salones'}</h2>
      <p className="small muted">
        {kids ? 'Los niños del grupo Niños entran solos al salón de su edad.' : 'Con rango de edad, cada persona entra sola al salón que le toca.'}
      </p>
      {data.status === 'loading' ? <Loading /> : null}
      {data.status === 'error' ? <ErrorState error={data.error} onRetry={data.reload} /> : null}
      {data.status === 'ready' ? (
        <>
          {data.data.items.length === 0 ? <p className="muted">Todavía no hay salones.</p> : null}
          {data.data.withoutBirthDate && data.data.withoutBirthDate.length > 0 ? (
            <Alert tone="info">
              Sin fecha de nacimiento no se puede asignar salón por edad ({data.data.withoutBirthDate.length}):{' '}
              {data.data.withoutBirthDate.slice(0, 20).map((p, i) => (
                <span key={p.personId}>
                  {i ? ', ' : ''}
                  <Link to={`/app/miembros/${p.personId}`}>{p.name}</Link>
                </span>
              ))}
              {data.data.withoutBirthDate.length > 20 ? '…' : ''}. Complétala en su ficha con «Editar datos».
            </Alert>
          ) : null}
          <ul className="ministry-grid">
            {data.data.items.map((c, i) => (
              <ClassroomCard key={c.id} c={c} index={i} />
            ))}
          </ul>
          {data.data.canCreate && !adding ? (
            <Button variant="secondary" onClick={() => setAdding(true)}>
              Nuevo salón
            </Button>
          ) : null}
          {adding ? <NewClassroomForm churchId={churchId} ministries={[{ id: ministryId, name: '', kids }]} onCancel={() => setAdding(false)} /> : null}
        </>
      ) : null}
    </section>
  );
}
